<template>
  <a v-if="href" :href="href" class="btn" :class="setClasses">
    <slot />
  </a>

  <router-link v-else-if="type === 'router'" :to="cLink(to)" class="btn" :class="setClasses">
    <slot />
  </router-link>

  <button v-else-if="type === 'dropdown'" class="btn --is-dropdown" :class="setClasses" type="button">
    <slot />
  </button>

  <button v-else :type="type" class="btn" :class="setClasses" @click="$emit('click', $event)">
    <slot />
  </button>
</template>

<script>
import { logicMixin } from '@/mixins/LogicMixin';

export default {
  name: 'Button',
  mixins: [logicMixin],
  props: {
    appearance: {
      type: String,
      default: 'secondary'
    },
    size: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'button'
    },
    href: {
      type: String,
      default: null
    },
    to: {
      type: String,
      default: null
    }
  },
  computed: {
    setClasses: function () {
      const classes = [];
      let appearance = [];

      if (this.appearance) {
        appearance = this.appearance.split(' ');

        appearance.forEach(function(value) {
          classes.push('--is-' + value);
        });
      }

      if (this.size) {
        classes.push('--is-' + this.size);
      }

      return classes.join(' ');
    }
  }
};
</script>

<style lang="scss" scoped>
/**
 * Button
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc LP Button
 * @since 2020-10-27
**/

// Variables
$component: 'btn';

// Variants
%primary {
  border-color: darken($primary, 2%);
  box-shadow: 0 1px 0 0 rgba(darken($primary, 10%), 0.1),
    inset 0 1px 0 1px rgba($white, 0.06);
  @include linear-gradient(to bottom, lighten($primary, 3%), $primary);
  color: $white;
}
%primary-hover {
  border-color: darken($primary, 4%);
  box-shadow: 0 1px 2px 0 rgba(darken($primary, 10%), 0.1),
    inset 0 1px 0 1px rgba($white, 0.06);
  @include linear-gradient(to bottom, $primary, darken($primary, 5%));
}
%primary-focus {
  outline: none;
}
%primary-active {
  border-color: darken($primary, 6%);
  background: darken($primary, 5%);
  box-shadow: inset 0 2px 1px 0 rgba(darken($primary, 12%), 0.54);
}
%primary-disabled {
  border-color: desaturate(lighten($primary, 20%), 25%);
  background: desaturate(lighten($primary, 25%), 25%);
  cursor: not-allowed;
}

%secondary {
  border-color: $sky-dark;
  box-shadow: 0 1px 0 0 rgba(22, 29, 37, 0.05);
  @include linear-gradient(to bottom, $white, $sky-lighter);
  color: $ink;
}
%secondary-hover {
  @include linear-gradient(to bottom, $sky-lighter, darken($sky-lighter, 3%));
}
%secondary-focus {
  outline: none;
}
%secondary-active {
  box-shadow: 0 0 0 0 transparent, inset 0 1px 1px 0 rgba($ink-lighter, 0.1),
    inset 0 1px 4px 0 rgba($ink-lighter, 0.2);
  background: $sky-light;
}
%secondary-disabled {
  border-color: $sky;
  background: $sky-lighter;
  color: $ink-lightest;
  cursor: not-allowed;
}

%tertiary {
  border-color: darken($ink-light, 2%);
  box-shadow: 0 1px 0 0 rgba(darken($ink-light, 10%), 0.1),
    inset 0 1px 0 1px rgba($white, 0.06);
  @include linear-gradient(to bottom, lighten($ink-light, 3%), $ink-light);
  color: $white;
}
%tertiary-hover {
  border-color: darken($ink-light, 4%);
  box-shadow: 0 1px 2px 0 rgba(darken($ink-light, 10%), 0.1),
    inset 0 1px 0 1px rgba($white, 0.06);
  @include linear-gradient(to bottom, $ink-light, darken($ink-light, 3%));
}
%tertiary-focus {
  outline: none;
}
%tertiary-active {
  border-color: darken($ink-light, 6%);
  background: darken($ink-light, 3%);
  box-shadow: inset 0 2px 1px 0 rgba(darken($ink-light, 12%), 0.54);
}
%tertiary-disabled {
  border-color: desaturate(lighten($ink-light, 20%), 25%);
  background: desaturate(lighten($ink-light, 25%), 25%);
  cursor: not-allowed;
}

%inline {
  border-color: transparent;
  box-shadow: none;
  color: $ink;
}
%inline-hover {
  border-color: transparent;
}
%inline-focus {
  outline: none;
}
%inline-active {
  background: $sky-lighter;
}
%inline-disabled {
  color: $ink-lightest;
  cursor: not-allowed;
}

// Component
.#{$component} {
  // display: inline-block;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  margin: 0;
  border: $btn-border-width solid transparent;
  @include padding-top(map-get($spacers, 3) / 3);
  @include padding-right(map-get($spacers, 5) / 2);
  @include padding-bottom(map-get($spacers, 3) / 3);
  @include padding-left(map-get($spacers, 5) / 2);
  border-radius: 50px;
  background-color: transparent;
  text-align: center;
  @include font-size($font-size-base);
  line-height: $line-height-base;
  font-weight: $font-weight-normal;
  // transition: all ease-in-out 150ms;

  // When has an icon
  .icon,
  ::v-deep .icon {
    // @include margin-right(map-get($spacers, 2));
    @include margin-left(map-get($spacers, 2));
    @include font-size($font-size-base * 1.1);
    line-height: 0;
    transform: scale(1.5);
    transition: all 0.2s ease-in-out;
    will-change: transform;
  }

  svg {
    @include rfs(1rem, width);
    @include rfs(1rem, height);
    @include margin-left(map-get($spacers, 2));
  }

  @include hover () {
    .icon-angle-right,
    ::v-deep .icon-angle-right {
      transform: scale(1.5) translateX(0.2rem);
    }
  }

  // Adjacent
  & + & {
    @include margin-left(map-get($spacers, 2));
  }

  // Sizes
  &.--is-small {
    @include padding-top(map-get($spacers, 3) / 3);
    @include padding-right(map-get($spacers, 5) / 2);
    @include padding-bottom(map-get($spacers, 3) / 3);
    @include padding-left(map-get($spacers, 5) / 2);
    @include font-size(map-get($landing-page, 'font-size-sm'));

    .icon,
    ::v-deep .icon {
      @include font-size($font-size-base * 1.2);
      transform: translateY(0.140rem);
    }

    svg {
      @include rfs(0.8rem, width);
      @include rfs(0.8rem, height);
    }
  }

  &.--is-large {
    @include padding-top(map-get($spacers, 3) / 2);
    @include padding-right(map-get($spacers, 4));
    @include padding-bottom(map-get($spacers, 3) / 2);
    @include padding-left(map-get($spacers, 4));
    @include font-size($font-size-lg);

    .icon,
    ::v-deep .icon {
      transform: scale(2);
    }

    svg {
      @include rfs(1.2rem, width);
      @include rfs(1.2rem, height);
    }

    @include hover () {
      .icon-angle-right,
      ::v-deep .icon-angle-right {
        transform: scale(2) translateX(0.2rem);
      }
    }
  }

  // Appearance variants
  &.--is-primary {
    @extend %primary;

    &:hover {
      @extend %primary-hover;
    }

    &:focus {
      @extend %primary-focus;
    }

    &:active:not(.--is-disabled):not(:disabled) {
      @extend %primary-active;
    }

    &:disabled,
    &.--is-disabled {
      @extend %primary-disabled;
    }

    &.--is-active {
      @extend %primary-active;
    }
  }

  &.--is-secondary {
    @extend %secondary;

    &:hover {
      @extend %secondary-hover;
    }

    &:focus {
      @extend %secondary-focus;
    }

    &:active:not(.--is-disabled):not(:disabled) {
      @extend %secondary-active;
    }

    &:disabled,
    &.--is-disabled {
      @extend %secondary-disabled;
    }

    &.--is-active {
      @extend %secondary-active;
    }
  }

  &.--is-tertiary {
    @extend %tertiary;

    &:hover {
      @extend %tertiary-hover;
    }

    &:focus {
      @extend %tertiary-focus;
    }

    &:active:not(.--is-disabled):not(:disabled) {
      @extend %tertiary-active;
    }

    &:disabled,
    &.--is-disabled {
      @extend %tertiary-disabled;
    }

    &.--is-active {
      @extend %tertiary-active;
    }
  }

  &.--is-inline {
    @extend %inline;

    &:hover {
      @extend %inline-hover;
    }

    &:focus {
      @extend %inline-focus;
    }

    &:active:not(.--is-disabled):not(:disabled) {
      @extend %inline-active;
    }

    &:disabled,
    &.--is-disabled {
      @extend %inline-disabled;
    }

    &.--is-active {
      @extend %inline-active;
    }
  }

  // Big button with two lines
  &.--is-extended {
    @include padding-top(map-get($spacers, 3) / 3);
    @include padding-bottom(map-get($spacers, 3) / 3);
    line-height: 130%;
    text-align: left;

    ::v-deep {
      > b {
        font-weight: $font-weight-bolder;
      }

      > b + small {
        display: block;
        font-weight: $font-weight-normal;
      }
    }
  }

  /* Dropdown */
  &.--is-dropdown {
    font-weight: $font-weight-normal;
    color: $ink-lighter;

    &:after {
      @include icomoon;
      content: '\e90f';
      @include margin-left(map-get($spacers, 1));
      @include font-size($font-size-lg * 1.4);
      line-height: 0;
      vertical-align: middle;
    }

    > span,
    > b,
    > strong {
      color: $ink;
    }
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-md')) {
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-sm')) {
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-xs')) {
    &.--is-extended {
      @include padding-top(map-get($spacers, 3) / 1.5);
      @include padding-bottom(map-get($spacers, 3) / 1.5);

      small,
      ::v-deep small {
        display: none !important;
      }
    }
  }
}
</style>
